import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { OrderModel } from "@insite/client-framework/Types/ApiModels";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    formattedDate: string;
    quoteDetails?: OrderModel;
    formattedExpDate: string;
    isExpireQuoteWarning: (dateVal: any) => boolean;
    isQuoteExpired: (dateVal: any) => boolean;
}

type Props = OwnProps;

export interface ADIOrderDetailsSummaryTableMobileStyles {
    attributeTypeWrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    attributeTypeLabelTextEven?: TypographyPresentationProps;
    attributeTypeValuesTextEven?: TypographyPresentationProps;
    firstSectionBoxMobile?: InjectableCss;
    orderSummaryGridItem?: InjectableCss;
    orderSummaryText?: TypographyProps;
}

export const adiOrderDetailsSummaryTableMobileStyles: ADIOrderDetailsSummaryTableMobileStyles = {
    orderSummaryText: {
        variant: "h2",
        css: css`
            font-size: 22px;
            line-height: 0.64;
            margin-top: 5px;
            margin-bottom: 16px;
            color: ${ADIColors.darkBlue1};
            font-weight: bold;
        `,
    },
    firstSectionBoxMobile: {
        css: css`
            display: none;
            @media only screen and (max-width: 1024px) {
                width: 100%;
                display: flex;
                flex-direction: column;
            }
        `,
    },
    attributeTypeLabelText: {
        css: css`
            word-break: break-word;
            background: ${ADIColors.white1};
        `,
    },
    attributeTypeValuesText: {
        css: css`
            background: ${ADIColors.white1};
            text-transform: capitalize;
        `,
    },
    attributeTypeLabelTextEven: {
        css: css`
            word-break: break-word;
            background: ${getColor("common.background")};
        `,
    },
    attributeTypeValuesTextEven: {
        css: css`
            background: ${getColor("common.background")};
            text-transform: capitalize;
        `,
    },
    attributeTypeWrapper: {
        css: css`
            width: 100%;
            display: grid;
            grid-template-columns: 35% 65%;
            border-radius: 6px;
            overflow: hidden;
            border: 1px solid ${ADIColors.gray3};
            > ${TypographyStyle} {
                padding: 15px 12px;
                font-size: 14px;
                line-height: 14px;
                color: ${getColor("text.main")};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        grid-template-columns: 40% 60%;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = adiOrderDetailsSummaryTableMobileStyles;
let isQuoteExpiredFlag = false;
let isExpireQuoteWarningFlag = false;

const ADIOrderDetailsSummaryTableMobile: FC<Props> = ({
    formattedDate,
    quoteDetails,
    formattedExpDate,
    isExpireQuoteWarning,
    isQuoteExpired,
}) => {
    const currentDate = new Date();

    const quoteExpired = (expiryDate: any) => {
        const expiry = new Date(expiryDate);

        if (expiry < currentDate) {
            return true;
        } else {
            return false;
        }
    };
    const customerPO = quoteDetails?.customerPO;
    const referenceNO = quoteDetails?.orderRefNum;
    const expiryDate = quoteDetails?.properties["expiryDate"];
    const projectStatus = quoteDetails?.properties["projectStatus"];
    const orderLinesLength = quoteDetails?.orderLines?.length;
    // isQuoteExpiredFlag = quoteExpired(expiryDate);
    isExpireQuoteWarningFlag = isExpireQuoteWarning(expiryDate);
    // isQuoteExpiredFlag = quoteExpired(expiryDate); ---- P21 Change
    isQuoteExpiredFlag = isQuoteExpired(expiryDate);

    const titleTextStyle = () => css`
        color: ${customerPO === "" ? ADIColors.gray3 : getColor("text.main")};
        margin-bottom: 5px;
        font-size: 16px;
        font-weight: bold;
        line-height: 1.13;
        background: ${projectStatus === "Y" ? ADIColors.white1 : getColor("common.background")};
        @media print {
            font-size: 14px;
            color: ${ADIColors.black};
        }
        @media only screen and (max-width: 767px) {
            font-weight: normal;
        }
    `;

    const orderPoStyle = () => css`
        color: ${customerPO === "" ? ADIColors.gray3 : getColor("text.main")};
        font-size: 14px;
        text-transform: capitalize;
        line-height: 1.43;
        font-weight: normal;
        background: ${projectStatus === "Y" ? ADIColors.white1 : getColor("common.background")};
        @media print {
            font-size: 13px;
            color: ${ADIColors.black};
        }
        @media (max-width: 1024px) {
            margin-bottom: 5px !important;
        }
    `;

    const referenceTextStyle = () => css`
        color: ${referenceNO === "" ? ADIColors.gray3 : getColor("text.main")};
        background: ${projectStatus === "Y" ? getColor("common.background") : ADIColors.white1};
        @media print {
            font-size: 14px;
            color: ${ADIColors.black};
        }
        @media only screen and (max-width: 767px) {
            font-weight: normal;
        }
        margin-bottom: 5px;
        font-size: 16px;
        font-weight: bold;
        line-height: 1.13;
    `;

    const referenceNumberStyle = () => css`
        color: ${referenceNO === "" ? ADIColors.gray3 : getColor("text.main")};
        background: ${projectStatus === "Y" ? getColor("common.background") : ADIColors.white1};
        font-size: 14px;
        text-transform: capitalize;
        line-height: 1.43;
        font-weight: normal;
        @media print {
            font-size: 13px;
            color: ${ADIColors.black};
        }
        @media (max-width: 1024px) {
            margin-bottom: 5px !important;
        }
    `;

    return (
        <StyledWrapper {...styles.firstSectionBoxMobile}>
            <Typography {...styles.orderSummaryText}>{translate("Quote Summary")}</Typography>
            <StyledWrapper {...styles.attributeTypeWrapper}>
                <Typography {...styles.attributeTypeLabelText}>{translate("Date")}</Typography>
                <Typography {...styles.attributeTypeValuesText}>{formattedDate}</Typography>

                {projectStatus === "Y" && (
                    <>
                        <Typography {...styles.attributeTypeLabelTextEven}>{translate("Project Name")}</Typography>
                        <Typography {...styles.attributeTypeValuesTextEven}>
                            {quoteDetails?.properties["projectName"]}
                        </Typography>
                    </>
                )}

                <Typography css={titleTextStyle()}>{translate("PO #")}</Typography>
                <Typography css={orderPoStyle()}>
                    {customerPO === "" ? translate("N/A") : customerPO?.toLowerCase()}
                </Typography>

                <Typography css={referenceTextStyle()}>{translate("Reference #")}</Typography>
                <Typography css={referenceNumberStyle()}>
                    {referenceNO === "" ? translate("N/A") : referenceNO?.toLowerCase()}
                </Typography>

                <Typography {...styles.attributeTypeLabelTextEven}>{translate("Exp. Date")}</Typography>
                <Typography {...styles.attributeTypeValuesTextEven}>
                    {formattedExpDate === "Invalid. Date" ? "" : formattedExpDate}

                    {(isQuoteExpiredFlag || isExpireQuoteWarningFlag) && !(orderLinesLength === 0) && (
                        <Typography as="span">
                            <img
                                alt="column-details-error"
                                className="column-details-error"
                                src="https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Quotelist/icon-warning.svg"
                            />
                        </Typography>
                    )}
                </Typography>
                {quoteDetails?.requestor && (
                    <>
                        <Typography {...styles.attributeTypeLabelText}>{translate("Requestor")}</Typography>
                        <Typography {...styles.attributeTypeLabelText}>{quoteDetails?.requestor}</Typography>
                    </>
                )}

                <Typography {...styles.attributeTypeLabelText}>{translate("Items")}</Typography>
                <Typography {...styles.attributeTypeValuesText}>
                    {quoteDetails?.invoiceOrderLines[0]?.invoiceOrderLines.length}
                </Typography>

                <Typography {...styles.attributeTypeLabelTextEven}>{translate("Material Total")}</Typography>
                <Typography {...styles.attributeTypeValuesTextEven}>
                    {quoteDetails?.invoiceOrderLines[0]?.amountDisplay}
                </Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default ADIOrderDetailsSummaryTableMobile;
